import { Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { fetchCoachesPollLatest } from '@/lib/api'
import { RankBadge } from '@/components/ui/RankBadge'
import { TeamAvatar } from '@/components/ui/TeamAvatar'
import { Skeleton } from '@/components/ui/Skeleton'

export default function CoachesPollPage() {
  const { sport, sportLabel } = useSport()
  const poll = useApi(() => fetchCoachesPollLatest(sport), [sport])

  if (poll.loading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="text" lines={1} className="h-8 w-64" />
        <Skeleton variant="card" className="h-96" />
      </div>
    )
  }

  if (!poll.data?.rows) {
    return (
      <div className="card p-12 text-center">
        <p className="text-slate-400">No coaches poll data available yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">{sportLabel} Coaches Poll</h1>
        <p className="text-sm text-slate-400 mt-1">
          United Soccer Coaches Poll{poll.data.poll_date && ` — ${new Date(poll.data.poll_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
        </p>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-pitch-700">
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3 w-16">Rank</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Team</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3 hidden sm:table-cell">Record</th>
              <th className="text-right text-xs uppercase tracking-wider text-slate-400 px-4 py-3 hidden md:table-cell">Points</th>
              <th className="text-right text-xs uppercase tracking-wider text-slate-400 px-4 py-3 hidden md:table-cell">1st</th>
              <th className="text-center text-xs uppercase tracking-wider text-slate-400 px-4 py-3 w-20">Trend</th>
            </tr>
          </thead>
          <tbody>
            {poll.data.rows.map((row) => (
              <tr key={row.rank} className="border-b border-pitch-800/50 hover:bg-pitch-800/40 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-mono font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.rank}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={row.team_slug ? `/${sport}/teams/${row.team_slug}` : '#'}
                    className="flex items-center gap-3 hover:text-accent transition-colors"
                  >
                    <TeamAvatar name={row.school_name} logoUrl={row.logo_url} colorSeed={row.conference_name} size="sm" />
                    <div>
                      <span className="text-sm font-medium text-slate-200">{row.school_name}</span>
                      {row.conference_name && (
                        <span className="text-xs text-slate-500 ml-2 hidden lg:inline">{row.conference_name}</span>
                      )}
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span className="text-sm font-mono text-slate-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.record}
                  </span>
                </td>
                <td className="px-4 py-3 text-right hidden md:table-cell">
                  <span className="text-sm font-mono text-slate-300" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.points}
                  </span>
                </td>
                <td className="px-4 py-3 text-right hidden md:table-cell">
                  <span className="text-sm font-mono text-slate-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.first_place_votes || '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <RankBadge rank={row.rank} prevRank={row.prev_rank} size="sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
